import { useEffect, useLayoutEffect, useRef } from 'react';

// ---------------------------------------------------------------------------
// View-state persistence
//
//   sessionStorage (VIEW_STATE_KEY) — full UI state, survives refresh only.
//   localStorage   (LAST_CHAT_KEY)  — last selected chat, survives tab close.
//
//   Refresh  → restore exact view (chat + overlays like settings / agents).
//   Fresh visit → restore just the last selected chat (or home).
// ---------------------------------------------------------------------------

const VIEW_STATE_KEY = 'agentica_view_state';
const LAST_CHAT_KEY = 'agentica_last_chat';

interface PersistedViewState {
  chatId: string | null;
  settings: boolean;
  settingsTab: 'general' | 'billing';
  agents: boolean;
}

export interface InitialState {
  view: PersistedViewState | null;
  chatId: string | null;
}

function readInitialState(): InitialState {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;

    if (nav?.type === 'reload') {
      const raw = sessionStorage.getItem(VIEW_STATE_KEY);
      if (raw) {
        const view = JSON.parse(raw) as PersistedViewState;
        return { view, chatId: view.chatId };
      }
    } else {
      sessionStorage.removeItem(VIEW_STATE_KEY);
    }

    const lastChatId = localStorage.getItem(LAST_CHAT_KEY);
    return { view: null, chatId: lastChatId };
  } catch {
    return { view: null, chatId: null };
  }
}

// Synchronously read persisted state once, before the component tree mounts.
// This value never changes — it captures what storage held at page load.
export const initialViewState: InitialState = readInitialState();

export const clearLastChat = () => {
  try { localStorage.removeItem(LAST_CHAT_KEY); } catch { /* */ }
};

export const clearLastChatIf = (sessionId: string) => {
  try {
    if (localStorage.getItem(LAST_CHAT_KEY) === sessionId) {
      localStorage.removeItem(LAST_CHAT_KEY);
    }
  } catch { /* */ }
};

export const clearAllPersistedState = () => {
  try { localStorage.removeItem(LAST_CHAT_KEY); } catch { /* */ }
  try { sessionStorage.removeItem(VIEW_STATE_KEY); } catch { /* */ }
};

export interface ViewPersistenceInput {
  chatId: string | null;
  showSettings: boolean;
  settingsTab: 'general' | 'billing';
  showAgentsPage: boolean;
}

export const useViewPersistence = (current: ViewPersistenceInput) => {
  const viewStateRef = useRef<PersistedViewState>({ chatId: null, settings: false, settingsTab: 'general', agents: false });

  useLayoutEffect(() => {
    try {
      const state: PersistedViewState = {
        chatId: current.chatId,
        settings: current.showSettings,
        settingsTab: current.settingsTab,
        agents: current.showAgentsPage,
      };
      viewStateRef.current = state;
      sessionStorage.setItem(VIEW_STATE_KEY, JSON.stringify(state));
      if (current.chatId) localStorage.setItem(LAST_CHAT_KEY, current.chatId);
      else localStorage.removeItem(LAST_CHAT_KEY);
    } catch { /* storage unavailable */ }
  }, [current.chatId, current.showSettings, current.settingsTab, current.showAgentsPage]);

  useEffect(() => {
    const flush = () => {
      try {
        const s = viewStateRef.current;
        sessionStorage.setItem(VIEW_STATE_KEY, JSON.stringify(s));
        if (s.chatId) localStorage.setItem(LAST_CHAT_KEY, s.chatId);
        else localStorage.removeItem(LAST_CHAT_KEY);
      } catch { /* storage unavailable */ }
    };
    window.addEventListener('beforeunload', flush);
    return () => window.removeEventListener('beforeunload', flush);
  }, []);
};
